import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import type { ImageGenTabProps } from "./types";
import { defaultPromptForType } from "./types";
import { useLocale } from "@/hooks/useLocale";
import { fmtDate, fmtTime } from "@/lib/locale-format";

export function DraftsTab(props: ImageGenTabProps) {
  const { locale, t } = useLocale();
  const {
    imageType, drafts, currentDraftId, savingDraft,
    loadDraft, handleDeleteDraft,
    setBriefText, setGenerationPrompt, setDrawingDataUrl,
    setHasCachedBrief, setGeneratedPreview,
  } = props;

  // Need access to web reference reset — use parent setters
  const handleNewDraft = () => {
    setBriefText("");
    setGenerationPrompt(defaultPromptForType[imageType] || "");
    setGeneratedPreview(null);
    setDrawingDataUrl(null);
    setHasCachedBrief(false);
    toast.success(t.draft_started);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {drafts.length} draft{drafts.length !== 1 ? "s" : ""} saved
          {savingDraft && <span className="ml-2 text-primary">saving…</span>}
          {currentDraftId && <span className="ml-2 text-primary">• active</span>}
        </p>
        <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1" onClick={handleNewDraft}>
          <Plus className="w-3 h-3" /> New
        </Button>
      </div>
      {drafts.length === 0 && (
        <p className="text-xs text-muted-foreground text-center py-6">
          No drafts yet. Start researching or generating to auto-save.
        </p>
      )}
      {drafts.map(d => (
        <div
          key={d.id}
          className={cn(
            "rounded-lg border p-3 space-y-2 cursor-pointer transition-all hover:border-primary/40",
            d.id === currentDraftId ? "border-primary/50 bg-primary/5" : "border-border"
          )}
          onClick={() => loadDraft(d)}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <Badge variant={d.status === "accepted" ? "default" : d.status === "generated" ? "secondary" : "outline"} className="text-[9px] h-4 px-1.5">
                  {d.status}
                </Badge>
                <span className="text-[10px] text-muted-foreground">
                  {fmtDate(d.updated_at, locale)} {fmtTime(d.updated_at, locale, { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
              {d.brief_text && (
                <p className="text-[11px] text-foreground mt-1 line-clamp-2">{d.brief_text.slice(0, 120)}</p>
              )}
              <div className="flex gap-2 mt-1 text-[10px] text-muted-foreground">
                {(d.web_reference_images as string[])?.length > 0 && <span>{(d.web_reference_images as string[]).length} refs</span>}
                {d.generation_prompt && <span>art direction</span>}
                {d.drawing_data_url && <span>drawing</span>}
              </div>
            </div>
            {d.generated_image_url && (
              <img src={d.generated_image_url} alt="" className="w-12 h-12 rounded-md object-cover shrink-0 border border-border" loading="lazy" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
            )}
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1 flex-1" onClick={(e) => { e.stopPropagation(); loadDraft(d); }}>
              <RefreshCw className="w-3 h-3" /> Load
            </Button>
            <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1 text-destructive" onClick={(e) => { e.stopPropagation(); handleDeleteDraft(d.id); }}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
